import { t } from "ttag";
import { z } from "zod";

import { userAttributesSchema } from "@/shared/models";
import { isValidDOB } from "@/shared/utils";
import { DOCUMENT_FOLDER } from "./legacy-constants";

const NANP_TEN_DIGIT_REGEX = /^[2-9]\d{2}[2-9]\d{6}$/;
const ZIP_CODE_REGEX = new RegExp(/^\d{5}(-\d{4})?$/);

export const trimmedEmail = z
  .string()
  .trim()
  .email({ error: () => t`Enter a valid email` })
  .min(1, { error: () => t`Email is required` });

export const dateOfBirthSchema = z
  .object({
    day: z
      .number()
      .int()
      .min(1, { error: () => t`Invalid day` })
      .max(31, { error: () => "Invalid day" }),
    month: z
      .number()
      .int()
      .min(1, { error: () => t`Invalid month` })
      .max(12, { error: () => t`Invalid month` }),
    year: z
      .number()
      .int()
      .min(1900, { error: () => t`Invalid year` })
      .max(new Date().getFullYear(), { error: () => t`Invalid year` }),
  })
  .refine(
    (dob) => {
      if (!dob.day || !dob.month || !dob.year) {
        return true;
      }

      return isValidDOB(dob);
    },
    {
      error: () => t`Invalid day for the selected month`,
      path: ["day"],
    },
  );

export const zipCodeSchema = z
  .string()
  .trim()
  .min(1, { error: () => t`Zip code is required` })
  .regex(ZIP_CODE_REGEX, {
    error: () => t`Zip code must be in the format 12345 or 12345-6789`,
  });

export const locationSchema = z.object({
  city: z.string().min(1, { error: () => t`City code is required` }),
  state: z.string().min(1, { error: () => t`State is required` }),
  zip: zipCodeSchema,
  country: z.string().min(1, { error: () => t`Country is required` }),
  line1: z
    .string()
    .min(1, { error: () => t`House and street number are required` }),
  line2: z.string().optional(),
});

const policySchema = z.object({
  cardFrontImage: z.string().optional(),
  cardBackImage: z.string().optional(),
  cardFrontId: z.string().optional().nullable(),
  cardBackId: z.string().optional().nullable(),
});

export const passwordSchema = z.object({
  password: z.string().min(1, { error: () => t`Password is required` }),
});

export const emailSchema = z.object({
  email: trimmedEmail,
});

export const confirmPasswordSchema = passwordSchema
  .and(
    z.object({
      passwordConfirmation: z.string(),
    }),
  )
  .refine((data) => data.password === data.passwordConfirmation, {
    error: () => t`Passwords must match`,
    path: ["passwordConfirmation"],
  });

export const phoneNumberSchema = z
  .string()
  .min(1, { error: () => t`Phone number is required` })

  .regex(NANP_TEN_DIGIT_REGEX, {
    error: () =>
      t`Enter a valid U.S. or Canadian phone number (no spaces or symbols)`,
  });

export const patientSchema = z
  .object({
    firstName: z.string().min(1, { error: () => t`First name is required` }),
    lastName: z.string().min(1, { error: () => t`Second name is required` }),
    gender: z.lazy(() => userAttributesSchema.shape.gender),
    dateOfBirth: z
      .string()
      .min(1, { error: () => t`Date of birth is required` }),
    email: trimmedEmail,
    phoneNumber: phoneNumberSchema,
    timezone: z.string().min(1, { error: () => t`Timezone is required` }),
    pharmacyName: z.string().optional(),
    pharmacyAddress: z.string().optional(),
    insuranceProvider: z.string().optional(),
    insuranceMemberId: z.string().optional(),
  })
  .and(locationSchema)
  .and(policySchema);

const tempUploadedSchema = z.array(
  z.object({
    id: z.string().min(1, {
      error: () => t`File required, try again or remove it`,
    }),
    name: z.string().min(1, { error: () => t`File name is required` }),
    file: z.instanceof(File),
  }),
);

export const uploadFilesSchema = z.object({
  type: z.enum(DOCUMENT_FOLDER),
  files: tempUploadedSchema,
});
